import { IndeterminateCheckbox } from "@bcc/components/ui/checkbox";
import { cn } from "@bcc/lib/utils";
import { flexRender, type Header, type RowSelectionState, type Table } from "@tanstack/react-table";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import * as React from "react";
import type { DataTableRow } from "./data-table.types";
import {
  setHeaderColumnTestId,
  setHeaderRowTestId,
  setHeaderSortButtonTestId,
  setHeaderTestId,
  setSelectAllCheckboxTestId,
} from "./data-table.utils";

interface DataTableHeaderProps<T extends object> {
  table: Table<DataTableRow<T>>;
  selectable?: boolean;
  showExpanderColumn?: boolean;
  maxSelectableRows?: number;
  setRowSelection?: (selection: RowSelectionState) => void;
}

/**
 * Sort indicator component
 */
function SortIndicator({ direction }: { direction: false | "asc" | "desc" }) {
  if (direction === "asc") {
    return <ChevronUp className="ml-1 size-4" />;
  }
  if (direction === "desc") {
    return <ChevronDown className="ml-1 size-4" />;
  }
  return <ChevronsUpDown className="ml-1 size-4 opacity-50" />;
}

/**
 * Individual header cell
 */
function HeaderCell<T extends object>({ header }: { header: Header<DataTableRow<T>, unknown> }) {
  const canSort = header.column.getCanSort();
  const isSorted = header.column.getIsSorted();

  if (header.isPlaceholder) {
    return null;
  }

  const content = flexRender(header.column.columnDef.header, header.getContext());

  if (!canSort) {
    return <div className="flex items-center">{content}</div>;
  }

  return (
    <button
      type="button"
      data-testid={setHeaderSortButtonTestId(header.id)}
      className="flex items-center hover:text-foreground transition-colors -ml-2 px-2 py-1 rounded-md hover:bg-muted/50"
      onClick={header.column.getToggleSortingHandler()}
    >
      {content}
      <SortIndicator direction={isSorted} />
    </button>
  );
}

/**
 * DataTableHeader component
 *
 * Renders the table header with sortable columns and optional select-all checkbox.
 */
export function DataTableHeader<T extends object>({
  table,
  selectable = false,
  showExpanderColumn = false,
  maxSelectableRows,
  setRowSelection,
}: DataTableHeaderProps<T>) {
  const prePaginationFlatRows = table.getPrePaginationRowModel().flatRows;
  const allLeafRows = React.useMemo(
    () => prePaginationFlatRows.filter((row) => !row.getIsGrouped() && !row.original.isDisabled),
    [prePaginationFlatRows]
  );

  const effectiveMax = maxSelectableRows ? Math.min(allLeafRows.length, maxSelectableRows) : allLeafRows.length;

  const rowSelection = table.getState().rowSelection;
  const selectedLeafCount = React.useMemo(
    () => allLeafRows.filter((row) => rowSelection[row.id] === true).length,
    [allLeafRows, rowSelection]
  );

  const isAllSelected = selectedLeafCount > 0 && selectedLeafCount >= effectiveMax;
  const isSomeSelected = selectedLeafCount > 0 && selectedLeafCount < effectiveMax;

  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      if (!setRowSelection) return;
      if (!checked) {
        setRowSelection({});
        return;
      }
      const newSelection: RowSelectionState = {};
      for (const row of allLeafRows.slice(0, effectiveMax)) {
        newSelection[row.id] = true;
      }
      setRowSelection(newSelection);
    },
    [allLeafRows, effectiveMax, setRowSelection]
  );

  return (
    <thead data-testid={setHeaderTestId()} className="border-b border-border bg-muted/30 sticky top-0 z-10">
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} data-testid={setHeaderRowTestId()}>
          {showExpanderColumn && <th className="w-[50px] px-4 py-3" />}
          {selectable && (
            <th className="w-[50px] px-4 py-3">
              <IndeterminateCheckbox
                testId={setSelectAllCheckboxTestId()}
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                disabled={allLeafRows.length === 0}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </th>
          )}
          {headerGroup.headers.map((header) => (
            <th
              key={header.id}
              data-testid={setHeaderColumnTestId(header.id)}
              className={cn(
                "px-4 py-3 text-left text-xs font-semibold text-muted-foreground  tracking-wider",
                header.column.getCanSort() && "cursor-pointer select-none"
              )}
              style={{
                width: header.getSize() !== 150 ? header.getSize() : undefined,
              }}
            >
              <HeaderCell header={header} />
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
}
